/**
 * Pure-CSS + SVG chart primitives.
 *
 * Built without a chart library so the bundle stays lean — TaskMagix
 * dashboards never need richer interactivity than what these support.
 * If we ever do, swap them for recharts/visx behind the same component
 * names and every caller keeps working.
 *
 * Exports:
 *   - <PieChart segments centerLabel centerValue size />
 *   - <DonutChart segments title centerLabel centerValue size />
 *   - <Pie3D segments width />  — 3D pie, variable-height slices + callouts
 *   - <HorizontalBarList rows max />
 *   - <RankedBarChart rows formatValue />  — ranked bars on a value axis
 *   - <Sparkline points width height />
 *   - <Legend entries />  — pairs with PieChart / DonutChart
 */

import { useId, useLayoutEffect, useState } from 'react'
import { useChartHover } from './use-chart-hover'
import { ChartTooltip } from './ChartTooltip'
import { DONUT_PALETTE } from './palette'
import { niceTicks } from './scale'
import { pie3dLayout } from './pie3d'
import { formatNumber } from '../../lib/format'

/** SVG path for a pie wedge (centre → arc → close). 0° at 12 o'clock. */
function pieSlicePath(cx, cy, r, startAngle, endAngle) {
  const toXY = (deg) => {
    const a = ((deg - 90) * Math.PI) / 180
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
  }
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  const [x1, y1] = toXY(startAngle)
  const [x2, y2] = toXY(endAngle)
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`
}

/** Build {start,end} angle spans for the non-zero segments of a total. */
function angleSpans(segments, total) {
  let acc = 0
  return segments
    .filter((s) => (s.value ?? 0) > 0)
    .map((s) => {
      const start = (acc / total) * 360
      acc += s.value
      const end = (acc / total) * 360
      return { ...s, start, end }
    })
}

/** Shared focus-on-hover slice class + hover-payload handlers, reused by
 *  PieChart and DonutChart so every wedge behaves identically. */
function sliceClass(s) {
  return (
    'transition-opacity duration-150 group-hover:opacity-40 hover:!opacity-100' +
    (s.onClick ? ' cursor-pointer' : '')
  )
}
function makeSliceHandlers(s, total, track, clear) {
  const pct = total ? Math.round(((s.value ?? 0) / total) * 100) : 0
  const payload = {
    color: s.color,
    label: s.label,
    value: `${formatNumber(s.value ?? 0)}${total ? ` · ${pct}%` : ''}`,
  }
  return {
    onMouseEnter: track(payload),
    onMouseMove: track(payload),
    onMouseLeave: clear,
    onClick: s.onClick,
  }
}

/**
 * Accessible name for a `role="img"` chart. A screen reader cannot read the
 * wedges, so the name has to carry the data: the centre chip when the caller
 * supplies one, otherwise the segment breakdown, so the chart is never
 * announced as a bare "image".
 */
function chartLabel(kind, centerLabel, centerValue, segments, total) {
  if (centerLabel) return `${kind}: ${centerLabel}, ${centerValue}`
  if (!total) return `${kind}: no data`
  const parts = segments
    .filter((s) => (s.value ?? 0) > 0)
    .map((s) => `${s.label ?? s.name ?? 'Unlabelled'} ${s.value}`)
  return `${kind}: ${parts.join(', ')}`
}

/**
 * Pie chart — solid conic-gradient disc with no inner hole. The total
 * (`centerValue` + `centerLabel`) renders as a small floating chip
 * over the pie's centre so the eye still picks up "X of Y" at a
 * glance without needing the donut's ring/hole geometry.
 *
 * Used by the **User panel dashboards** (Manager / Team Leader /
 * Employee) only. The Company Admin (`/dashboard`) and Super Admin
 * (`/super-admin/dashboard`) panels deliberately keep `DonutChart`
 * because their dashboards are denser and the donut's centre value
 * (e.g. "1,247 Members") needs to read clearly without competing
 * with adjacent legend rows. The User-panel dashboard has fewer,
 * larger cards and the pie reads better there.
 *
 *   <PieChart
 *     segments={[
 *       { label: 'Documents', value: 12, color: '#02367B' },
 *       { label: 'Images',    value: 5,  color: '#0496C7' },
 *     ]}
 *     centerLabel="Used"
 *     centerValue="120 MB"
 *   />
 */
export function PieChart({
  segments,
  centerLabel,
  centerValue,
  size = 140,
}) {
  const total = segments.reduce((a, s) => a + (s.value ?? 0), 0)
  const cx = size / 2
  const cy = size / 2
  const r = size / 2
  const { ref, hover, track, clear } = useChartHover()
  const slices = total > 0 ? angleSpans(segments, total) : []
  // The name belongs on the `role="img"` <svg>, not on the wrapper <div> —
  // axe flags the svg as unnamed otherwise, and the centre chip is only a
  // fallback anyway: it is absent on several callers. Falling back to the
  // segment breakdown means the chart is never announced as just "image".
  const ariaLabel = chartLabel('Pie chart', centerLabel, centerValue, segments, total)
  return (
    <div
      ref={ref}
      className="relative shrink-0"
      style={{ width: size, height: size }}
    >
      {/* Each wedge is its own SVG element so it can dim its siblings on
          hover + report into the shared tooltip — same behaviour as the
          donut. (Was a single conic-gradient disc with no interaction.) */}
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="group block drop-shadow-sm"
        role="img"
        aria-label={ariaLabel}
      >
        {total === 0 ? (
          <circle cx={cx} cy={cy} r={r} fill="var(--color-rule)" />
        ) : slices.length === 1 ? (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={slices[0].color}
            stroke="var(--chart-outline)"
            strokeWidth={1}
            className={sliceClass(slices[0])}
            {...makeSliceHandlers(slices[0], total, track, clear)}
          />
        ) : (
          slices.map((s) => (
            <path
              key={s.id ?? s.label}
              d={pieSlicePath(cx, cy, r, s.start, s.end)}
              fill={s.color}
              stroke="var(--chart-outline)"
              strokeWidth={1}
              className={sliceClass(s)}
              {...makeSliceHandlers(s, total, track, clear)}
            />
          ))
        )}
      </svg>
      {/* Floating centre chip — keeps the total readable. pointer-events-none
          so hovering over it still registers on the wedge beneath. */}
      {centerValue != null || centerLabel ? (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center justify-center bg-white/95 dark:bg-[color:var(--color-card)] rounded-full shadow-md border border-[color:var(--color-rule)] px-3 py-1.5 backdrop-blur-sm">
            {centerValue != null ? (
              <p className="font-display text-base tabular-nums text-[color:var(--color-ink-strong)] leading-none">
                {typeof centerValue === 'number'
                  ? formatNumber(centerValue)
                  : centerValue}
              </p>
            ) : null}
            {centerLabel ? (
              <p className="mt-0.5 text-[10px] uppercase tracking-wider text-[color:var(--color-ink-muted)] font-semibold">
                {centerLabel}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      {hover ? <ChartTooltip {...hover} bounds={size} /> : null}
    </div>
  )
}

/**
 * Donut chart rendered as a single conic-gradient ring + a centered
 * label/value. Best for 2–6 categories. Total of `segments.value` is
 * computed internally; pass colours via `segments[i].color`.
 *
 *   <DonutChart
 *     segments={[
 *       { label: 'Free', value: 12, color: '#10B981' },
 *       { label: 'Paid', value: 5,  color: '#F59E0B' },
 *     ]}
 *     centerLabel="Total"
 *     centerValue={17}
 *   />
 */
/** SVG path for one donut slice (outer arc → inner arc → close). Angles
 *  in degrees, 0° at 12 o'clock, sweeping clockwise. */
function donutSlicePath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const toXY = (r, deg) => {
    const a = ((deg - 90) * Math.PI) / 180
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
  }
  const largeArc = endAngle - startAngle > 180 ? 1 : 0
  const [ox1, oy1] = toXY(rOuter, startAngle)
  const [ox2, oy2] = toXY(rOuter, endAngle)
  const [ix2, iy2] = toXY(rInner, endAngle)
  const [ix1, iy1] = toXY(rInner, startAngle)
  return [
    `M ${ox1} ${oy1}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${ox2} ${oy2}`,
    `L ${ix2} ${iy2}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${ix1} ${iy1}`,
    'Z',
  ].join(' ')
}

export function DonutChart({
  segments,
  centerLabel,
  centerValue,
  size = 140,
  thickness = 22,
}) {
  const total = segments.reduce((a, s) => a + (s.value ?? 0), 0)
  const cx = size / 2
  const cy = size / 2
  const rOuter = size / 2
  const rInner = size / 2 - thickness
  const rMid = (rOuter + rInner) / 2
  const inner = size - thickness * 2
  const centerText =
    centerValue == null
      ? null
      : typeof centerValue === 'number'
        ? formatNumber(centerValue)
        : String(centerValue)
  // ~0.6em per glyph across ~78% of the hole; capped at the old text-2xl
  // (24px), floored at 13px.
  const centerFontPx = centerText
    ? Math.round(Math.max(13, Math.min(24, (inner * 0.78) / (centerText.length * 0.6))))
    : 24
  const { ref, hover, track, clear } = useChartHover()

  // Each slice is its own SVG element (was a single conic-gradient ring)
  // so it dims its siblings on hover + reports into the shared tooltip +
  // can drill through on click.
  const arcs = total > 0 ? angleSpans(segments, total) : []
  const ariaLabel = chartLabel('Donut chart', centerLabel, centerValue, segments, total)

  return (
    <div
      ref={ref}
      className="relative shrink-0"
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="group block"
        role="img"
        aria-label={ariaLabel}
      >
        {total === 0 ? (
          <circle
            cx={cx}
            cy={cy}
            r={rMid}
            fill="none"
            stroke="var(--color-rule)"
            strokeWidth={thickness}
          />
        ) : arcs.length === 1 ? (
          <>
            <circle
              cx={cx}
              cy={cy}
              r={rMid}
              fill="none"
              stroke={arcs[0].color}
              strokeWidth={thickness}
              className={sliceClass(arcs[0])}
              {...makeSliceHandlers(arcs[0], total, track, clear)}
            />
            {/* Hairline edges keep the single ring legible on the dark
             *  canvas (the outline token is transparent in light mode). */}
            <circle
              cx={cx}
              cy={cy}
              r={rOuter}
              fill="none"
              stroke="var(--chart-outline)"
              strokeWidth={1}
              className="pointer-events-none"
            />
            <circle
              cx={cx}
              cy={cy}
              r={rInner}
              fill="none"
              stroke="var(--chart-outline)"
              strokeWidth={1}
              className="pointer-events-none"
            />
          </>
        ) : (
          arcs.map((s) => (
            <path
              key={s.id ?? s.label}
              d={donutSlicePath(cx, cy, rOuter, rInner, s.start, s.end)}
              fill={s.color}
              stroke="var(--chart-outline)"
              strokeWidth={1}
              className={sliceClass(s)}
              {...makeSliceHandlers(s, total, track, clear)}
            />
          ))
        )}
      </svg>
      <div
        className="absolute rounded-full flex flex-col items-center justify-center pointer-events-none"
        style={{
          inset: thickness,
          width: inner,
          height: inner,
        }}
      >
        {centerText != null ? (
          // Sized to FIT the hole: a long value ("226 KB", "1.24 GB") shrinks
          // instead of spilling over the ring — in px, so the app's font-size
          // setting can't push it past the ring either. Short values ("158")
          // keep the old text-2xl size.
          <p
            className="font-display tabular-nums text-[color:var(--color-ink-strong)] leading-none whitespace-nowrap"
            style={{ fontSize: centerFontPx }}
          >
            {centerText}
          </p>
        ) : null}
        {centerLabel ? (
          <p className="mt-1 text-xs uppercase tracking-wider text-[color:var(--color-ink-muted)] font-semibold">
            {centerLabel}
          </p>
        ) : null}
      </div>
      {hover ? <ChartTooltip {...hover} bounds={size} /> : null}
    </div>
  )
}

/**
 * 3D pie with callouts — slices extruded to different heights (taller =
 * larger value) so the cut faces between them show, shaded walls, and a
 * leader line from each slice to a circular badge whose ring fills to that
 * slice's share (layout + painter's order in `pie3d.js`). Hovering any face
 * of a slice highlights the whole slice (others fade) and shows the shared
 * tooltip (value · share). Any colour format works (hex or `var(--token)`);
 * shading is a dark/light overlay, so it suits every palette. Pair it with
 * `Legend` for the exact counts.
 *
 * Flat variant (`uniform` + `callout="text"`): every slice the same thick
 * extrusion, and each slice's % + name + count printed beside the pie (no
 * legend needed). `formatValue(v)` renders the count line.
 *
 *   <Pie3D segments={[{ label: 'Completed', value: 61, color: '#059669' }]} />
 *   <Pie3D segments={plans} uniform callout="text" formatValue={(v) => `${v} companies`} />
 */
export function Pie3D({ segments, width = 320, uniform = false, callout = 'badge', formatValue }) {
  const { ref, hover, track, clear } = useChartHover()
  const [active, setActive] = useState(null)
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const ids = { wall: `p3w-${uid}`, gloss: `p3g-${uid}` }
  const geo = pie3dLayout(segments, { uniform, callout })
  const showValue = (v) => (formatValue ? formatValue(v) : formatNumber(Number(v)))
  const clip = (t, n = 20) => (t.length > n ? `${t.slice(0, n - 1)}…` : t)
  const ariaLabel = chartLabel('3D pie chart', null, null, segments ?? [], geo.total)
  const handlers = (s) => {
    const { onMouseEnter, onMouseMove, onMouseLeave } = makeSliceHandlers(s, geo.total, track, clear)
    return {
      onMouseEnter: (e) => {
        setActive(s.index)
        onMouseEnter(e)
      },
      onMouseMove,
      onMouseLeave: (e) => {
        setActive(null)
        onMouseLeave(e)
      },
    }
  }
  const faded = (i) => active != null && active !== i
  return (
    <div ref={ref} className="relative mx-auto w-full" style={{ maxWidth: width }}>
      <svg
        viewBox={`0 0 ${geo.width} ${geo.height}`}
        className="block h-auto w-full"
        role="img"
        aria-label={ariaLabel}
      >
        <defs>
          {/* Walls darken left → right-lit; cut faces darker still. */}
          <linearGradient id={ids.wall} gradientUnits="userSpaceOnUse" x1={geo.cx - geo.rx} y1="0" x2={geo.cx + geo.rx} y2="0">
            <stop offset="0" stopColor="#000000" stopOpacity="0.32" />
            <stop offset="1" stopColor="#000000" stopOpacity="0.1" />
          </linearGradient>
          <radialGradient id={ids.gloss} cx="35%" cy="20%" r="85%">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.3" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
        {geo.total === 0 ? (
          <ellipse cx={geo.cx} cy={geo.cy} rx={geo.rx} ry={geo.ry} fill="var(--color-rule)" />
        ) : null}
        {geo.faces.map((f, n) => {
          const s = geo.slices[f.slice]
          return (
            <g
              key={`${f.kind}-${f.slice}-${n}`}
              className="transition-opacity duration-150"
              style={{ opacity: faded(f.slice) ? 0.35 : 1 }}
              {...handlers(s)}
            >
              {/* Only tops get an edge (separates slices); walls stay
               *  seamless across the stacked height levels. */}
              <path
                d={f.d}
                fill={s.color}
                stroke={f.kind === 'top' ? 'var(--color-card)' : 'none'}
                strokeWidth={0.8}
                strokeLinejoin="round"
              />
              {f.kind === 'top' ? (
                <>
                  <path d={f.d} fill={`url(#${ids.gloss})`} className="pointer-events-none" />
                  {/* Dark mode: the same white hairline the donuts use, so
                   *  the darkest navies don't vanish into the dark card. */}
                  <path d={f.d} fill="none" stroke="var(--chart-outline)" strokeWidth={0.6} className="pointer-events-none" />
                </>
              ) : (
                <path
                  d={f.d}
                  fill={f.kind === 'wall' ? `url(#${ids.wall})` : '#000000'}
                  fillOpacity={f.kind === 'wall' ? 1 : 0.24}
                  className="pointer-events-none"
                />
              )}
            </g>
          )
        })}
        {/* Text callouts (flat variant) — colour chip + % + name + count. */}
        {geo.slices
          .filter((s) => s.textAt)
          .map((s) => {
            const { x, y, anchor } = s.textAt
            const chipX = anchor === 'start' ? x - 9 : x + 5
            const name = String(s.label ?? '')
            return (
              <g
                key={`t-${s.index}`}
                className="transition-opacity duration-150"
                style={{ opacity: faded(s.index) ? 0.35 : 1 }}
                {...handlers(s)}
              >
                <title>{`${name}: ${showValue(s.value)} (${Math.round(s.share * 100)}%)`}</title>
                <rect x={chipX} y={y - 19} width={4} height={38} rx={2} fill={s.color} stroke="var(--chart-outline)" strokeWidth={0.5} />
                <text
                  x={x}
                  y={y - 7}
                  textAnchor={anchor}
                  className="tabular-nums"
                  style={{ fontSize: 18, fontWeight: 700 }}
                  fill="var(--color-ink-strong)"
                >
                  {Math.round(s.share * 100)}
                  <tspan style={{ fontSize: 11, fontWeight: 600 }}>%</tspan>
                </text>
                <text x={x} y={y + 7} textAnchor={anchor} style={{ fontSize: 10.5, fontWeight: 600 }} fill="var(--color-ink)">
                  {clip(name)}
                </text>
                <text x={x} y={y + 19} textAnchor={anchor} className="tabular-nums" style={{ fontSize: 10 }} fill="var(--color-ink-muted)">
                  {clip(showValue(s.value))}
                </text>
              </g>
            )
          })}
        {/* Callouts — leader line + badge ringing the slice's share. */}
        {geo.slices.filter((s) => s.badge).map((s) => {
          const r = geo.badgeR
          const circ = 2 * Math.PI * (r - 4)
          const pct = Math.round(s.share * 100)
          return (
            <g
              key={`c-${s.index}`}
              className="transition-opacity duration-150"
              style={{ opacity: faded(s.index) ? 0.35 : 1 }}
              {...handlers(s)}
            >
              <path d={s.leader} fill="none" stroke={s.color} strokeWidth={1.2} />
              <circle cx={s.badge.x} cy={s.badge.y} r={r} fill="var(--color-card)" stroke="var(--color-rule)" strokeWidth={1} />
              <circle cx={s.badge.x} cy={s.badge.y} r={r - 4} fill="none" stroke="var(--color-rule)" strokeWidth={4} />
              <circle
                cx={s.badge.x}
                cy={s.badge.y}
                r={r - 4}
                fill="none"
                stroke={s.color}
                strokeWidth={4}
                strokeLinecap="round"
                strokeDasharray={`${Math.max(0.01, s.share) * circ} ${circ}`}
                transform={`rotate(-90 ${s.badge.x} ${s.badge.y})`}
              />
              <text
                x={s.badge.x}
                y={s.badge.y}
                textAnchor="middle"
                dominantBaseline="central"
                className="tabular-nums"
                style={{ fontSize: 13, fontWeight: 700 }}
                fill="var(--color-ink-strong)"
              >
                {pct}
                <tspan style={{ fontSize: 8, fontWeight: 600 }} fill="var(--color-ink-muted)">
                  %
                </tspan>
              </text>
            </g>
          )
        })}
      </svg>
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}

/**
 * Legend that pairs with DonutChart. Pass the same `segments` array so
 * colours stay in sync, plus a `formatValue` that decides what reads
 * to the right of each label (count, percentage, both).
 */
export function Legend({ segments, formatValue }) {
  const total = segments.reduce((a, s) => a + (s.value ?? 0), 0)
  return (
    <ul className="space-y-2 flex-1 min-w-0">
      {segments.map((s) => {
        const pct = total === 0 ? 0 : Math.round((s.value / total) * 100)
        // Show the raw count by default — the donut itself already encodes
        // proportion visually, so a percentage next to the count reads as
        // an ambiguous "7% 1" when the eye scans the column. Callers that
        // prefer a percent pass a custom `formatValue`.
        const valueEl = formatValue ? (
          // `shrink-0` (parity with the plain branch below) — the value
          // must keep its width so a squeezed row truncates the LABEL
          // (which has `truncate`) instead of mangling "201 MB".
          <span className="text-[color:var(--color-ink-strong)] font-semibold tabular-nums shrink-0">
            {/* Third arg = the segment itself, so callers can attach a
             *  precomputed per-segment display (e.g. the storage
             *  donut's rounding-reconciled byte strings). Additive —
             *  existing two-arg callers are unaffected. */}
            {formatValue(s.value, pct, s)}
          </span>
        ) : (
          <span className="text-[color:var(--color-ink-strong)] font-semibold tabular-nums shrink-0">
            {formatNumber(s.value)}
          </span>
        )
        const dot = (
          <span
            aria-hidden="true"
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{
              backgroundColor: s.color,
              border: '1px solid var(--chart-outline)',
            }}
          />
        )
        const label = (
          <span className="text-[color:var(--color-ink)] flex-1 truncate">
            {s.label}
          </span>
        )
        // A segment carrying an `onClick` renders as a button so the row
        // can drill through (e.g. a Work Role slice → the filtered Members
        // list). Plain segments keep the original static row markup.
        return (
          <li key={s.id ?? s.label}>
            {s.onClick ? (
              <button
                type="button"
                onClick={s.onClick}
                title={s.title ?? `View ${s.label}`}
                className="flex w-full items-center gap-2 text-sm text-left -mx-1 px-1 py-0.5 rounded transition-colors cursor-pointer hover:bg-[color:var(--color-card-hover)]"
              >
                {dot}
                {label}
                {valueEl}
              </button>
            ) : (
              <div className="flex items-center gap-2 text-sm">
                {dot}
                {label}
                {valueEl}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Horizontal bar list — one row per entry. Used for "X by Department"
 * style breakdowns. `max` is the divisor for bar width; defaults to
 * the row with the highest value so the longest bar always reaches
 * 100% of the track.
 *
 *   <HorizontalBarList
 *     rows={[
 *       { label: 'Engineering', value: 12 },
 *       { label: 'Design',      value: 5  },
 *     ]}
 *   />
 */
export function HorizontalBarList({
  rows,
  max,
  formatValue,
  emptyMessage = 'No data yet.',
}) {
  const { ref, hover, track, clear } = useChartHover()
  if (!rows || rows.length === 0) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        {emptyMessage}
      </p>
    )
  }
  const effectiveMax =
    max != null
      ? max
      : Math.max(1, ...rows.map((r) => r.value ?? 0))
  const totalAll = rows.reduce((a, r) => a + (r.value ?? 0), 0)
  return (
    <div ref={ref} className="relative">
      {/* `group` so hovering one row dims the others (focus effect) — same
          behaviour as the donut/pie slices. */}
      <ul className="group space-y-3">
        {rows.map((r, i) => {
          const pct = effectiveMax === 0 ? 0 : (r.value / effectiveMax) * 100
          const share = totalAll ? Math.round(((r.value ?? 0) / totalAll) * 100) : 0
          const shown = formatValue
            ? formatValue(r.value)
            : formatNumber(r.value ?? 0)
          const payload = {
            color: r.color ?? 'var(--color-accent)',
            label: r.label,
            value: `${shown}${totalAll ? ` · ${share}%` : ''}`,
          }
          return (
            <li
              key={r.label + '_' + i}
              className="transition-opacity duration-150 group-hover:opacity-40 hover:!opacity-100"
              onMouseEnter={track(payload)}
              onMouseMove={track(payload)}
              onMouseLeave={clear}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-[color:var(--color-ink)] truncate">
                  {r.label}
                </span>
                <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0 ml-2">
                  {shown}
                </span>
              </div>
              <div
                className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden"
                role="progressbar"
                aria-valuenow={r.value}
                aria-valuemin={0}
                aria-valuemax={effectiveMax}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${Math.min(100, Math.max(0, pct))}%`,
                    backgroundColor: r.color ?? 'var(--color-accent)',
                  }}
                />
              </div>
            </li>
          )
        })}
      </ul>
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}

/**
 * Ranked bar chart — category labels on the left, one rounded bar per row
 * (pass rows largest-first), and a value axis underneath with "nice" ticks
 * (`niceTicks`) and vertical gridlines. Bars take the shared categorical
 * palette by rank (or `row.color`), outlined with `--chart-outline` so the
 * dark navies stay visible in dark mode — the same convention as the donuts.
 * Hover dims the other rows and shows the value + share of the total.
 *
 *   <RankedBarChart
 *     rows={[
 *       { label: 'Engineering', value: 15 },
 *       { label: 'DevOps',      value: 8  },
 *     ]}
 *     formatValue={(v) => `${v} tasks`}
 *   />
 *
 * `size` sets the bar thickness; the bar is centred in its row, so labels
 * stay aligned:
 *   'default' — the bar fills its row;
 *   'slim'    — slightly thinner (Company Admin + Sub Admin "Tasks by Department");
 *   'thin'    — about half as thick (user dashboards' "Workload by Space").
 */
const RANKED_BAR_SIZES = {
  default: { row: 'h-8', bar: 'h-full' },
  slim: { row: 'h-7', bar: 'h-6' },
  thin: { row: 'h-7', bar: 'h-3.5' },
}
export function RankedBarChart({ rows, formatValue, emptyMessage = 'No data yet.', size = 'default' }) {
  const { ref, hover, track, clear } = useChartHover()
  if (!rows || rows.length === 0) {
    return (
      <p className="text-sm text-[color:var(--color-ink-muted)] py-6 text-center italic">
        {emptyMessage}
      </p>
    )
  }
  const ticks = niceTicks(Math.max(0, ...rows.map((r) => r.value ?? 0)))
  const top = ticks[ticks.length - 1]
  const total = rows.reduce((a, r) => a + (r.value ?? 0), 0)
  const show = (v) => (formatValue ? formatValue(v) : formatNumber(v ?? 0))
  const { row: ROW, bar: BAR } = RANKED_BAR_SIZES[size] ?? RANKED_BAR_SIZES.default
  return (
    <div ref={ref} className="relative">
      <div className="flex gap-3">
        {/* Category labels — right-aligned against the axis, truncating
            (full name in the title) so a long name never squeezes the plot. */}
        <ul className="m-0 flex max-w-[40%] shrink-0 list-none flex-col gap-3 p-0" aria-hidden="true">
          {rows.map((r, i) => (
            <li
              key={r.label + '_' + i}
              className={`${ROW} flex min-w-0 items-center justify-end text-sm font-medium text-[color:var(--color-ink)]`}
              title={r.label}
            >
              <span className="truncate">{r.label}</span>
            </li>
          ))}
        </ul>
        <div className="relative min-w-0 flex-1">
          {/* Gridlines at every tick (the 0 line doubles as the axis). */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            {ticks.map((t) => (
              <span
                key={t}
                className="absolute inset-y-0 w-px bg-[color:var(--color-rule)]"
                style={{ left: `${(t / top) * 100}%` }}
              />
            ))}
          </div>
          {/* `group` so hovering one bar dims the others — same focus
              effect as HorizontalBarList and the donut slices. */}
          <ul className="group relative m-0 flex list-none flex-col gap-3 p-0">
            {rows.map((r, i) => {
              const v = r.value ?? 0
              const pct = top ? (v / top) * 100 : 0
              const share = total ? Math.round((v / total) * 100) : 0
              const color = r.color ?? DONUT_PALETTE[i % DONUT_PALETTE.length]
              const payload = {
                color,
                label: r.label,
                value: `${show(v)}${total ? ` · ${share}%` : ''}`,
              }
              return (
                <li
                  key={r.label + '_' + i}
                  className={`${ROW} flex items-center transition-opacity duration-150 group-hover:opacity-40 hover:!opacity-100`}
                  aria-label={`${r.label}: ${show(v)}${total ? `, ${share}% of total` : ''}`}
                  onMouseEnter={track(payload)}
                  onMouseMove={track(payload)}
                  onMouseLeave={clear}
                >
                  {v > 0 ? (
                    <span
                      className={`${BAR} rounded-l-[3px] rounded-r-full transition-[width] duration-500`}
                      style={{
                        width: `${Math.min(100, Math.max(1, pct))}%`,
                        backgroundColor: color,
                        boxShadow: 'inset 0 0 0 1px var(--chart-outline)',
                      }}
                    />
                  ) : null}
                </li>
              )
            })}
          </ul>
          {/* Value axis — tick labels under their gridlines. */}
          <div className="relative mt-2 h-4" aria-hidden="true">
            {ticks.map((t, i) => (
              <span
                key={t}
                className="absolute top-0 text-[11px] tabular-nums text-[color:var(--color-ink-muted)]"
                style={{
                  left: `${(t / top) * 100}%`,
                  transform:
                    i === 0 ? 'none' : i === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                }}
              >
                {formatNumber(t)}
              </span>
            ))}
          </div>
        </div>
      </div>
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}

/**
 * Tiny line/area sparkline. Pass a `points` array of numbers and the
 * SVG renders a smooth poly-line over an optional fill. No axes / no
 * labels — meant for inline use next to a KPI value.
 */
export function Sparkline({
  points,
  width = 120,
  height = 32,
  stroke = 'var(--color-accent)',
  fill = 'rgba(28,114,73,0.12)',
}) {
  if (!points || points.length === 0) return null
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  // Map each point to (x, y) on the SVG canvas, leaving a 1px breath
  // top/bottom so the stroke isn't clipped.
  const step = points.length > 1 ? width / (points.length - 1) : 0
  const coords = points.map((p, i) => {
    const x = i * step
    const y = height - 1 - ((p - min) / range) * (height - 2)
    return [x, y]
  })
  const linePath = coords
    .map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`))
    .join(' ')
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Trend over time"
      className="overflow-visible"
    >
      <path d={areaPath} fill={fill} />
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Multi-series line + area trend chart for dashboards. Responsive — it
 * measures its own width (ResizeObserver) and draws in real pixels, so
 * lines, dots and labels never distort the way a stretched `viewBox`
 * would. Light baseline gridlines, sparse x labels, a hover guide, and a
 * per-day tooltip (both series on one card). Built-in empty state when
 * there's no data or every point is zero. Reuses the shared
 * `useChartHover` + `ChartTooltip` so it behaves like every other chart.
 *
 *   labels       string[]                       — one x step each (day keys)
 *   series       [{ name, color, data:number[] }] — 1+ series, aligned to labels
 *   height       number (px, default 150)
 *   labelFormat  (dayKey) => string              — x-axis + tooltip label
 *   valueFormat  (n) => string
 *   emptyMessage string
 */
export function TrendChart({
  labels,
  series = [],
  height = 150,
  labelFormat = (d) => d,
  valueFormat = (v) => formatNumber(v ?? 0),
  emptyMessage = 'No activity in this period',
}) {
  const { ref, hover, track, clear } = useChartHover()
  const [width, setWidth] = useState(0)
  // Measure in a layout effect (before paint) so the chart draws at its real
  // width on the first visible frame — no flash of the empty placeholder while
  // a post-paint ResizeObserver catches up.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    setWidth(el.clientWidth || 0)
    if (typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(() => setWidth(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])

  const n = labels?.length ?? 0
  const cleanSeries = (series ?? []).filter(
    (s) => Array.isArray(s.data) && s.data.length === n,
  )
  const allZero =
    cleanSeries.length === 0 ||
    cleanSeries.every((s) => s.data.every((v) => !v))

  if (n === 0 || allZero) {
    return (
      <div
        ref={ref}
        className="flex items-center justify-center"
        style={{ height }}
      >
        <p className="text-sm text-[color:var(--color-ink-muted)] italic text-center px-4">
          {emptyMessage}
        </p>
      </div>
    )
  }

  const PAD_X = 6
  const PAD_T = 10
  const PAD_B = 22
  const w = Math.max(width, 2)
  const plotW = Math.max(1, w - PAD_X * 2)
  const plotH = Math.max(1, height - PAD_T - PAD_B)
  const maxVal = Math.max(1, ...cleanSeries.flatMap((s) => s.data))
  const xAt = (i) => (n === 1 ? w / 2 : PAD_X + (i * plotW) / (n - 1))
  const yAt = (v) => PAD_T + plotH - ((v ?? 0) / maxVal) * plotH
  const baseY = PAD_T + plotH

  const gridVals = [0, maxVal / 2, maxVal]
  const labelIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1]
  const line = (data) =>
    data
      .map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`)
      .join(' ')
  const area = (data) =>
    `${line(data)} L${xAt(n - 1).toFixed(1)},${baseY.toFixed(1)} L${xAt(0).toFixed(1)},${baseY.toFixed(1)} Z`
  const bandW = plotW / Math.max(1, n - 1)
  const hoverIdx = hover?._i

  return (
    <div ref={ref} className="relative w-full" style={{ height }}>
      {width > 0 ? (
        <svg width={w} height={height} role="img" aria-label="Trend over time">
          {gridVals.map((v, i) => (
            <line
              key={`g${i}`}
              x1={PAD_X}
              x2={w - PAD_X}
              y1={yAt(v)}
              y2={yAt(v)}
              stroke="var(--color-rule)"
              strokeWidth="1"
              opacity={i === 0 ? 0.85 : 0.35}
            />
          ))}
          {cleanSeries.map((s, si) => (
            <g key={`s${si}`}>
              <path d={area(s.data)} fill={s.color} opacity="0.1" stroke="none" />
              <path
                d={line(s.data)}
                fill="none"
                stroke={s.color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx={xAt(n - 1)} cy={yAt(s.data[n - 1])} r="3" fill={s.color} />
            </g>
          ))}
          {hoverIdx != null ? (
            <g>
              <line
                x1={xAt(hoverIdx)}
                x2={xAt(hoverIdx)}
                y1={PAD_T}
                y2={baseY}
                stroke="var(--color-rule-strong)"
                strokeWidth="1"
              />
              {cleanSeries.map((s, si) => (
                <circle
                  key={`hp${si}`}
                  cx={xAt(hoverIdx)}
                  cy={yAt(s.data[hoverIdx])}
                  r="4"
                  fill="var(--color-card)"
                  stroke={s.color}
                  strokeWidth="2"
                />
              ))}
            </g>
          ) : null}
          {labelIdx.map((i) => (
            <text
              key={`xl${i}`}
              x={xAt(i)}
              y={height - 6}
              textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
              fill="var(--color-ink-muted)"
              style={{ fontSize: 10 }}
            >
              {labelFormat(labels[i])}
            </text>
          ))}
          {labels.map((d, i) => {
            const payload = {
              _i: i,
              color: cleanSeries[0]?.color,
              label: labelFormat(d),
              value: cleanSeries[0]
                ? `${cleanSeries[0].name}: ${valueFormat(cleanSeries[0].data[i])}`
                : undefined,
              // Every further series on one line (renders exactly as before
              // for the 2-series charts; N-series charts list them all).
              hint:
                cleanSeries.length > 1
                  ? cleanSeries
                      .slice(1)
                      .map((s) => `${s.name}: ${valueFormat(s.data[i])}`)
                      .join(' · ')
                  : undefined,
            }
            return (
              <rect
                key={`h${i}`}
                x={xAt(i) - bandW / 2}
                y={PAD_T}
                width={bandW}
                height={plotH}
                fill="transparent"
                onMouseEnter={track(payload)}
                onMouseMove={track(payload)}
                onMouseLeave={clear}
              />
            )
          })}
        </svg>
      ) : null}
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}
